import { toast } from '@approvalmax/ui/src/components';
import { dateTimeHelpers } from '@approvalmax/utils';
import { useSetAtom } from 'jotai';
import { selectors, statics } from 'modules/common';
import { domain } from 'modules/data';
import { useUserProfile } from 'modules/utils';
import moment from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { routingService } from 'services/routing';
import {
    useGetDefaultTwoFaEnforcementType,
    useGetNetSuiteApiFilter,
    useUpdateCompany,
    useUpdateNetSuiteFilter,
} from 'shared/data/webApp/v1';
import { useGetCompanyCurrencies } from 'shared/data/webApp/v2';
import { useExpandedCompany } from 'shared/hooks/useExpandedCompany';
import { getPath, Path } from 'urlBuilder';

import { messages } from '../../messages';
import { enforcementOptions } from './OrganisationInfo.config';
import { compareByCodeLowerCase, filterTwoFaEnforcementOptions } from './OrganisationInfo.helpers';
import { savingOrganisationState } from './OrganisationInfo.state';
import { OrganisationInfoFormData } from './OrganisationInfo.types';

export const useOrganisationInfoForm = (company: selectors.types.ExpandedCompany) => {
    const { data: netSuiteApiFilter } = useOrganisationNetSuiteApiFilter();
    const { enforcementOptions, enforcementIsLoading } = useOrganisationInfoFormTwoFaEnforcement(company);

    const saveOrganisation = useSaveOrganisation(company);
    const { lockDate } = useOrganisationLockDate(company);
    const { financialYearEnd } = useOrganisationFinancialYearEnd(company);

    const form = useForm<OrganisationInfoFormData>({
        defaultValues: {
            companyId: company.id,
            name: company.name,
            enforcementTfaType: company.enforcementTfaType,
            timeZone: company.timeZone,
            defaultCurrency: company.defaultCurrency,
            isExpandAccount: false,
            taxCodeListsInclude: '',
            lockDate,
            financialYearEnd,
        },
    });

    useEffect(() => {
        if (enforcementOptions && !enforcementIsLoading) {
            const option = enforcementOptions.find((option) => option.id === company.enforcementTfaType);

            if (!option) {
                form.setValue('enforcementTfaType', enforcementOptions[0].id);
            }
        }
    }, [company.enforcementTfaType, enforcementIsLoading, enforcementOptions, form]);

    useEffect(() => {
        if (netSuiteApiFilter) {
            form.setValue('isExpandAccount', !!netSuiteApiFilter.apiFilter.isExpandAccount);
            form.setValue('taxCodeListsInclude', netSuiteApiFilter.apiFilter.taxCodeListsInclude);
        }
    }, [form, netSuiteApiFilter]);

    return {
        form,
        saveOrganisation,
        onFormSubmit: form.handleSubmit(saveOrganisation),
    };
};

export const useOrganisationInfoFormTwoFaEnforcement = (company: selectors.types.ExpandedCompany) => {
    const integrationType = company.integration?.integrationType || domain.IntegrationType.None;
    const isCompanyTrial = company.flags.isTrialLicense;

    const { data: enforcementValue, isLoading: enforcementIsLoading } = useGetDefaultTwoFaEnforcementType({
        path: { integrationType },
    });

    const filteredEnforcementOptions = useMemo(
        () => (isCompanyTrial ? enforcementOptions : filterTwoFaEnforcementOptions(enforcementValue?.enforcementType)),
        [enforcementValue?.enforcementType, isCompanyTrial]
    );

    return {
        enforcementOptions: filteredEnforcementOptions,
        enforcementIsLoading: !isCompanyTrial && enforcementIsLoading,
    };
};

export const useOrganisationLockDate = (company: selectors.types.ExpandedCompany) => {
    const { integration } = company;

    const showLockDate = integration && integration.integrationType !== domain.IntegrationType.None;
    const lockDate =
        showLockDate && integration && integration.lockDate ? moment.utc(integration.lockDate).format('ll') : '';

    return {
        lockDate,
        showLockDate,
    };
};

export const useOrganisationFinancialYearEnd = (company: selectors.types.ExpandedCompany) => {
    const { integration } = company;

    const showFinancialYearEnd = integration?.integrationType === domain.IntegrationType.Xero;
    const financialYearEnd =
        showFinancialYearEnd &&
        integration &&
        integration?.financialYearEnd &&
        typeof integration?.financialYearEnd === 'object'
            ? dateTimeHelpers.formatDate(
                  {
                      ...integration.financialYearEnd,
                      // backend sends month starting counting with 1
                      month: integration.financialYearEnd.month - 1,
                      // we hardcoded the leap year to show any date without problem. If we set 2021, then we would have a problem with the February 29
                      year: 2020,
                  },
                  'DayAndMonth'
              )
            : '';

    return {
        financialYearEnd,
        showFinancialYearEnd,
    };
};

export const useOrganisationNetSuiteApiFilter = () => {
    const { companyId } = useParams<{ companyId: string }>();

    const company = useExpandedCompany(companyId);
    const isNetSuite = company?.integration?.integrationType === domain.IntegrationType.NetSuite;

    return useGetNetSuiteApiFilter({ query: { companyId: company?.id } }, { enabled: isNetSuite });
};

export const useOrganisationCurrencies = (company: selectors.types.ExpandedCompany) => {
    const { data } = useGetCompanyCurrencies({
        path: { companyId: company.id },
    });

    const currencies = useMemo(
        () =>
            (data?.currencies || []).sort(compareByCodeLowerCase).map((currency) => {
                const code = currency.code.toLocaleLowerCase();
                const text = currency.isCrypto
                    ? statics.currency.findCryptoCurrencyByCode(code)?.text
                    : statics.currency.findCurrencyByCode(code)?.text;

                return {
                    id: currency.code,
                    text: text || code,
                };
            }),
        [data?.currencies]
    );

    return { currencies };
};

export const useSaveOrganisation = (company: selectors.types.ExpandedCompany) => {
    const me = useUserProfile();

    const form = useFormContext<OrganisationInfoFormData>();

    const setSavingOrganisation = useSetAtom(savingOrganisationState);

    const { mutateAsync: updateCompany } = useUpdateCompany();
    const { mutateAsync: updateNetSuiteFilter } = useUpdateNetSuiteFilter();

    const isNetSuite = company.integration?.integrationType === domain.IntegrationType.NetSuite;

    const saveNetSuiteFilter = useCallback(
        async (data: OrganisationInfoFormData) => {
            if (isNetSuite) {
                await updateNetSuiteFilter({
                    body: {
                        companyId: data.companyId,
                        apiFilter: {
                            isExpandAccount: data.isExpandAccount,
                            taxCodeListsInclude: data.taxCodeListsInclude,
                        },
                    },
                });
            }
        },
        [isNetSuite, updateNetSuiteFilter]
    );

    return async (data: OrganisationInfoFormData) => {
        try {
            setSavingOrganisation(true);

            await updateCompany({
                body: company.isReadonly
                    ? {
                          companyId: data.companyId,
                          name: data.name,
                          enforcementTfaType: data.enforcementTfaType,
                      }
                    : {
                          companyId: data.companyId,
                          name: data.name,
                          enforcementTfaType: data.enforcementTfaType,
                          defaultCurrency: data.defaultCurrency,
                          timeZone: data.timeZone,
                      },
            });

            await saveNetSuiteFilter(data);

            const isTwoFaEnforcementUpdated = data.enforcementTfaType !== company.enforcementTfaType;
            const isTwoFaEnforcementHard = data.enforcementTfaType === domain.TwoFaEnforcementType.Hard;

            if (isTwoFaEnforcementUpdated && form?.formState?.isDirty) {
                toast.success(
                    messages.enforcementSuccessInfo({
                        enforcementType: data.enforcementTfaType,
                    })
                );
            }

            if (isTwoFaEnforcementUpdated && isTwoFaEnforcementHard && !me.is2faEnabled && !me.isSsoLogin) {
                setTimeout(() => {
                    routingService.reloadToUrl(getPath(Path.twoFaHardEnforcement));
                }, 1000);
            }

            toast.success(messages.yourOrganisationHasBeenSaved);
        } catch {
            toast.error(messages.errorMessage);
        } finally {
            setSavingOrganisation(false);
        }
    };
};
